import * as React from "react";
import {
    Typography,
    Box,
    Grid,
    Button as MUIButton,
    Autocomplete,
    CircularProgress,
    Paper,
    Container,
    ToggleButtonGroup,
    ToggleButton
} from "@mui/material";
import { connect } from "react-redux";
import {
    useNavigate,
} from "react-router-dom";
import {
    MainInput,
    main_button,
} from "../../app/components/Styles";
import { FoCard, FoChips } from "./financeUi";
import { brandFilterOptions, brandOptionLabel } from "../../app/components/BrandSelect";
import { getTransactions, getVendors, extractInvoice, getCashCycles } from "../../app/store/actions/whFinanceActions";
import { useEffect } from "react";
import { Description } from "@mui/icons-material";
import moment from "moment";
import DateRangePicker from "../../app/components/DateRangePicker";

export const PrintInvoices = ({
    getVendors,
    extractInvoice,
    getCashCycles,
    vendors,
    cashCycles,
    isLoading
}) => {
    const navigate = useNavigate();
    const [extractionMode, setExtractionMode] = React.useState("dateRange"); // "dateRange" or "cashCycle"
    const [startDate, setStartDate] = React.useState(moment().startOf("month"))
    const [endDate, setEndDate] = React.useState(moment().endOf("month"))
    const [selectedCashCycle, setSelectedCashCycle] = React.useState(null)
    const [vendor, setVendor] = React.useState(null)

    const formatDateParam = (date) => date?.format?.("YYYY-MM-DD") ?? date;

    const handleExtract = () => {
        if (extractionMode === "dateRange") {
            extractInvoice({
                startDate: formatDateParam(startDate),
                endDate: formatDateParam(endDate),
                vendor: vendor?._id,
            }, navigate);
        } else {
            extractInvoice({ cashCycle: selectedCashCycle?.title, vendor: vendor?._id }, navigate);
        }
    }

    const handleModeChange = (newMode) => {
        if (newMode) setExtractionMode(newMode);
    }

    useEffect(() => {
        getVendors({})
        getCashCycles({})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <FoCard title="Extract a PDF" note="This builds a PDF for one brand. An invoice register with send, remind, and payment status is not available yet.">
                <Box sx={{ mt: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <FoChips
                            value={extractionMode}
                            onChange={handleModeChange}
                            options={[
                                { value: "dateRange", label: "Date range" },
                                { value: "cashCycle", label: "Cash cycle" },
                            ]}
                        />
                        {extractionMode === "dateRange" && (
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    mt: 2, 
                                    color: 'text.secondary',
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic'
                                }}
                            >
                                All transactions between this date range will be included.
                            </Typography>
                        )}
                        {extractionMode === "cashCycle" && (
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    mt: 2, 
                                    color: 'text.secondary',
                                    fontSize: '0.875rem',
                                    fontStyle: 'italic'
                                }}
                            >
                                All transactions tagged to this cash cycle will be included. This will include only orders and related fees and payouts (i.e. no additional fees will be included).
                            </Typography>
                        )}
                    </Box>

                    <Box 
                        sx={{ 
                            p: 3,
                            borderRadius: 2,
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            mb: 4
                        }}
                    >
                        <Grid container spacing={3}>
                            {extractionMode === "dateRange" ? (
                                <Grid item xs={12}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            mb: 1.5, 
                                            fontWeight: 600, 
                                            color: 'text.primary',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        Date Range *
                                    </Typography>
                                    <DateRangePicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChange={({ startDate: s, endDate: e }) => {
                                            if (s) setStartDate(s);
                                            if (e) setEndDate(e);
                                        }}
                                        fullWidth
                                        margin="dense"
                                        placeholder="Select date range"
                                    />
                                </Grid>
                            ) : (
                                <Grid item xs={12} sm={6}>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            mb: 1.5, 
                                            fontWeight: 600, 
                                            color: 'text.primary',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        Cash Cycle ID *
                                    </Typography>
                                    <Autocomplete
                                        options={cashCycles || []}
                                        required
                                        value={selectedCashCycle}
                                        onChange={(_, value) => setSelectedCashCycle(value)}
                                        getOptionLabel={(cashCycle) => cashCycle.title}
                                        renderInput={(params) => (
                                            <MainInput
                                                {...params}
                                                fullWidth
                                                placeholder="Select Cash Cycle"
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} sm={extractionMode === "dateRange" ? 12 : 6}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        mb: 1.5, 
                                        fontWeight: 600, 
                                        color: 'text.primary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    Vendor *
                                </Typography>
                                <Autocomplete
                                    options={vendors || []}
                                    required
                                    value={vendor}
                                    onChange={(_, value) => setVendor(value)}
                                    getOptionLabel={brandOptionLabel}
                                    filterOptions={brandFilterOptions}
                                    renderInput={(params) => (
                                        <MainInput
                                            {...params}
                                            fullWidth
                                            placeholder="Select Vendor"
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <MUIButton 
                            sx={{ 
                                ...main_button, 
                                minWidth: 180, 
                                py: 1.75,
                                px: 4,
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                                }
                            }} 
                            disabled={
                                isLoading || 
                                !vendor || 
                                (extractionMode === "dateRange" && (!startDate || !endDate)) ||
                                (extractionMode === "cashCycle" && !selectedCashCycle)
                            } 
                            onClick={() => handleExtract()}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                "Extract Invoices"
                            )}
                        </MUIButton>
                    </Box>
                </Box>
        </FoCard>
    );
};

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
    token: state?.auth?.token,
    orders: state?.records?.orders,
    vendors: state?.records?.vendors,
    payouts: state?.records?.payouts,
    cashCycles: state?.records?.cashCycles
});

const mapDispatchToProps = { getTransactions, getVendors, extractInvoice, getCashCycles };

export default connect(mapStateToProps, mapDispatchToProps)(PrintInvoices);
